import { useEffect, useState } from 'react';
import { ArrowUpRight, X } from 'lucide-react';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AuthDialog({
  open,
  onClose,
  onLogin,
  onRegister,
  busy,
  error,
  registration = {},
  brandName = 'ZaoMeng',
}) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', agreed: false });
  const [localError, setLocalError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [editable, setEditable] = useState({ email: false, password: false, confirmPassword: false });
  const registrationOpen = registration.enabled !== false;

  useEffect(() => {
    if (!registrationOpen && mode === 'register') setMode('login');
  }, [mode, registrationOpen]);

  if (!open) return null;

  const update = (key) => (event) => {
    setLocalError('');
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };
  const selectMode = (nextMode) => {
    setMode(nextMode);
    setLocalError('');
  };
  const unlock = (key) => () => setEditable((current) => ({ ...current, [key]: true }));
  const submit = (event) => {
    event.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setLocalError('请输入有效的邮箱地址。');
      return;
    }
    if (mode === 'register' && (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password))) {
      setLocalError('密码至少 8 位，并且必须同时包含字母和数字。');
      return;
    }
    if (mode === 'register' && form.password !== form.confirmPassword) {
      setLocalError('两次输入的密码不一致。');
      return;
    }
    if (mode === 'register' && !form.agreed) {
      setLocalError('请先阅读并同意用户协议与隐私政策。');
      return;
    }
    setForm((current) => ({ ...current, email }));
    if (mode === 'login') onLogin({ email, password: form.password });
    else onRegister({ email, password: form.password, acceptedTerms: true });
  };

  const workingText = mode === 'register' ? '正在创建...' : '正在登录...';

  return (
    <div className="auth-screen">
      <section className="auth-panel" role="dialog" aria-modal="true" aria-labelledby="canvas-auth-title">
        {onClose && <button className="auth-close" type="button" onClick={onClose} aria-label="关闭登录窗口"><X size={19} /></button>}
        <div className="auth-panel__intro">
          <span className="auth-mark">Z</span>
          <span className="auth-kicker">ZAOMENG / ACCESS</span>
          <h1 id="canvas-auth-title">登录或注册</h1>
          <p>{registrationOpen ? '开启你的图片与视频创作空间' : '注册暂未开放，已有账号可直接登录。'}</p>
        </div>
        <div className="auth-tabs">
          <button type="button" className={mode === 'login' ? 'is-active' : ''} onClick={() => selectMode('login')}>登录</button>
          {registrationOpen && <button type="button" className={mode === 'register' ? 'is-active' : ''} onClick={() => selectMode('register')}>注册账号</button>}
        </div>
        <form onSubmit={submit} className="auth-form">
          <label>
            <span>邮箱</span>
            <input name="zaomeng-user-email" type="email" inputMode="email" value={form.email} onChange={update('email')} onFocus={unlock('email')} readOnly={!editable.email} autoComplete="section-zaomeng-user email" placeholder="name@example.com" required />
          </label>
          <label>
            <span>密码</span>
            <div className="auth-password">
              <input name="zaomeng-user-password" type={passwordVisible ? 'text' : 'password'} value={form.password} onChange={update('password')} onFocus={unlock('password')} readOnly={!editable.password} autoComplete={`section-zaomeng-user ${mode === 'login' ? 'current-password' : 'new-password'}`} placeholder={mode === 'register' ? '至少 8 位，含字母和数字' : '请输入密码'} required />
              <button type="button" onClick={() => setPasswordVisible((current) => !current)}>{passwordVisible ? '隐藏' : '显示'}</button>
            </div>
          </label>
          {mode === 'register' && (
            <>
              <label>
                <span>确认密码</span>
                <div className="auth-password">
                  <input name="zaomeng-user-password-confirm" type={confirmVisible ? 'text' : 'password'} value={form.confirmPassword} onChange={update('confirmPassword')} onFocus={unlock('confirmPassword')} readOnly={!editable.confirmPassword} autoComplete="section-zaomeng-user new-password" placeholder="再次输入密码" required />
                  <button type="button" onClick={() => setConfirmVisible((current) => !current)}>{confirmVisible ? '隐藏' : '显示'}</button>
                </div>
              </label>
              <label className="auth-agreement">
                <input type="checkbox" checked={form.agreed} onChange={(event) => { setLocalError(''); setForm((current) => ({ ...current, agreed: event.target.checked })); }} />
                <span>我已阅读并同意 <a href="/terms.html" target="_blank" rel="noreferrer">用户协议</a> 与 <a href="/privacy.html" target="_blank" rel="noreferrer">隐私政策</a></span>
              </label>
            </>
          )}
          {(localError || error) && <p className="form-error" role="alert" aria-live="polite">{localError || error}</p>}
          <button className="auth-submit" type="submit" disabled={busy} aria-busy={busy}>
            <span>{busy ? workingText : mode === 'login' ? '登录' : '创建账号'}</span><ArrowUpRight size={17} />
          </button>
        </form>
        {mode === 'login' && <p className="auth-terms">登录前可查看造梦的 <a href="/terms.html" target="_blank" rel="noreferrer">用户协议</a> 与 <a href="/privacy.html" target="_blank" rel="noreferrer">隐私政策</a></p>}
      </section>
      <span className="auth-footnote">{brandName} / Generative Workspace</span>
    </div>
  );
}
